import { DynamicQuestionnaireField } from '@core/data-access/models/api/dynamic-questionnaire-fields.model';
import { FieldKey, FieldType, OptionKey } from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import {
  CheckboxFormField,
  FormField,
  FormFields,
  FormFieldValue
} from '@core/data-access/models/form/form-store.model';
import { FormValue } from '@core/data-access/models/form/form.model';
import { Person, PersonType } from '@core/data-access/models/person.model';
import { isCheckboxFormField } from '@core/utils/form-fields/form-field-type-guard';
import {
  cloneDeep,
  concat,
  every,
  forEach,
  get,
  includes,
  isDate,
  isEmpty,
  isNil,
  isNull,
  keys,
  last,
  mapValues,
  omit,
  pickBy,
  reduce,
  split
} from 'lodash';

export const isSendToOwnerFieldChanged = (formFields: FormFields): boolean =>
  includes(keys(formFields), FieldKey.SendToOwner);

export const isGDPRConsentFieldChecked = (formFields: FormFields): boolean =>
  get(formFields, [FieldKey.GDPR, 'value', OptionKey.GDPRConsent, 'value']);

export const isEmptyFormField = (value: string | number | object | Date) =>
  (isEmpty(value) || isNull(value)) && !isDate(value);

export const setDisabledFormFields = (
  person: Person,
  formFields: FormFields,
  skipFields: FieldKey[] = [FieldKey.GDPR, FieldKey.CV]
) =>
  reduce(
    cloneDeep(formFields),
    (disabledFormFields, formField, fieldKey) => {
      disabledFormFields[fieldKey] = formField;

      if (includes(skipFields, fieldKey)) {
        return disabledFormFields;
      }

      const isSavedTicketHolder = !isNil(person?.id);
      const isPersonTypeBuyer = includes([PersonType.Buyer, PersonType.BuyerIsHolder], person?.type);

      if (isSavedTicketHolder || isPersonTypeBuyer) {
        if (isCheckboxFormField(formField)) {
          forEach(formField.value, (checkboxValue, checkboxKey) => {
            disabledFormFields[fieldKey] = {
              ...formField,
              isDisabled: true,
              value: {
                [checkboxKey]: {
                  ...checkboxValue,
                  isDisabled: true
                }
              }
            };
          });
        } else {
          disabledFormFields[fieldKey].isDisabled = !isEmptyFormField(formField.value as string);
        }
      }

      return disabledFormFields;
    },
    {} as FormFields
  );

export const setIsSavedFormFields = (formFields: FormFields, isSaved = true) =>
  mapValues(formFields, (formField: FormField) => ({
    ...formField,
    isSaved
  }));

export const enableDisabledFormFields = (formFields: FormFields) =>
  reduce(
    cloneDeep(formFields),
    (enabledFormFields, formField, fieldKey) => {
      enabledFormFields[fieldKey] = formField;

      if (isCheckboxFormField(formField)) {
        forEach(formField.value, (checkboxValue, checkboxKey) => {
          if (isNil(checkboxValue?.isDisabled)) {
            return;
          }

          enabledFormFields[fieldKey] = {
            ...formField,
            isDisabled: false,
            value: {
              [checkboxKey]: {
                ...checkboxValue,
                isDisabled: false
              }
            }
          };
        });
      } else {
        enabledFormFields[fieldKey].isDisabled = false;
      }

      return enabledFormFields;
    },
    {} as FormFields
  );

export const disableAddressFormField = (formFields: FormFields) => {
  if (isNil(formFields[FieldKey.Address])) {
    formFields[FieldKey.Address] = {
      value: '',
      isQuestionnaire: false
    };
  }

  formFields[FieldKey.Address].isDisabled = every(
    [FieldKey.Company, FieldKey.Country, FieldKey.Street, FieldKey.ZipCode, FieldKey.City],
    (fieldKey) => !isEmpty(get(formFields, [fieldKey, 'value']))
  );

  return formFields;
};

export const disablePrefilledSavedBuyerFormFields = (formFields: FormFields, savedBuyerFormFields: FormFields) =>
  reduce(
    concat(keys(formFields), keys(savedBuyerFormFields)),
    (updatedFormFields, fieldKey) => {
      const isFormFieldSkipped = includes(
        [
          FieldKey.Address,
          FieldKey.Password,
          FieldKey.VerifyPassword,
          FieldKey.CreateAccount,
          FieldKey.CreateAccountButton,
          FieldKey.Newsletter,
          FieldKey.FairCatalogue,
          FieldKey.DifferentBillingAddress
        ],
        fieldKey
      );

      if (isFormFieldSkipped) {
        if (isNil(formFields[fieldKey])) {
          return updatedFormFields;
        }

        updatedFormFields[fieldKey] = formFields[fieldKey];

        return updatedFormFields;
      }

      if (isNil(savedBuyerFormFields)) {
        return updatedFormFields;
      }

      const savedBuyerFormField = savedBuyerFormFields[fieldKey];

      if (isNil(savedBuyerFormField)) {
        return updatedFormFields;
      }

      const { value, isSaved } = savedBuyerFormField as FormField;

      const fieldValue = isDate(value) ? value.toString() : value;
      const isDisabled = !isEmpty(fieldValue) && isSaved;

      updatedFormFields[fieldKey] = {
        ...savedBuyerFormField,
        isDisabled
      };

      return updatedFormFields;
    },
    {} as FormFields
  );

export const removeFormFields = (formFields: FormFields, removeFields: FieldKey[]) => omit(formFields, removeFields);

export const removeIsHiddenFromFormFields = (formFields: FormFields) =>
  mapValues(formFields, (formField) => omit(formField, 'isHidden')) as Partial<
    Record<FieldKey, FormField | CheckboxFormField>
  >;

export const removeQuestionnaireFormFields = (formFields: FormFields) => pickBy(formFields, { isQuestionnaire: false });

export const removeNonQuestionnaireFormFields = (formFields: FormFields) =>
  pickBy(formFields, { isQuestionnaire: true });

export const getQuestionnaireFormFieldDynValueFieldKey = (value: FormFieldValue | FormValue) =>
  parseInt(last(split(value as string, '.')));

export const setQuestionnaireFormFieldKey = ({ id, type, values: [{ id: textFieldId }] }: DynamicQuestionnaireField) =>
  type === FieldType.TextInput ? `${id}_${textFieldId}` : id.toString();
